import type { ITenantThemeConfig } from '@/shared/api/types/tenant-types'
import { Loader2, Upload, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateTenantThemeMutation } from '@/entities/tenant'
import { MAX_BRANDING_IMAGE_SIZE_BYTES, MAX_BRANDING_IMAGE_SIZE_MB } from '@/shared/constants/media'
import { THEME_PREVIEW_DEBOUNCE_MS } from '@/shared/constants/timing'
import { uploadFileWithPresign } from '@/shared/api/services/upload-service'
import { AVAILABLE_FONTS } from '@/shared/constants/fonts'
import { TENANT_THEME_DEFAULT_COLORS } from '@/shared/constants/theme-colors'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface TenantThemeConfigProps {
  tenantId: string
  initialTheme?: ITenantThemeConfig
  onThemeChange?: (theme: ITenantThemeConfig) => void
  onDirtyChange?: (isDirty: boolean) => void
  onSaveReady?: (ctx: { onSave: () => void; disabled: boolean; isPending: boolean }) => void
}

interface ThemeFormState {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  fontFamily: string
  logoUrl: string
  faviconUrl: string
}

export function TenantThemeConfig({
  tenantId,
  initialTheme,
  onThemeChange,
  onDirtyChange,
  onSaveReady,
}: TenantThemeConfigProps) {
  const { t } = useTranslation()
  const mutation = useUpdateTenantThemeMutation()
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const buildFormFromTheme = useCallback((theme?: ITenantThemeConfig): ThemeFormState => {
    return {
      primaryColor: theme?.primaryColor ?? TENANT_THEME_DEFAULT_COLORS.primary,
      secondaryColor: theme?.secondaryColor ?? TENANT_THEME_DEFAULT_COLORS.secondary,
      accentColor: theme?.accentColor ?? TENANT_THEME_DEFAULT_COLORS.accent,
      fontFamily: theme?.fontFamily ?? (AVAILABLE_FONTS[0] as string),
      logoUrl: theme?.logoUrl ?? '',
      faviconUrl: theme?.faviconUrl ?? '',
    }
  }, [])

  const [form, setForm] = useState<ThemeFormState>(() => buildFormFromTheme(initialTheme))
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify(buildFormFromTheme(initialTheme)),
  )
  const [isLogoUploading, setIsLogoUploading] = useState(false)
  const [isFaviconUploading, setIsFaviconUploading] = useState(false)
  const [previewImage, setPreviewImage] = useState<{ url: string; alt: string } | null>(null)

  useEffect(() => {
    const next = buildFormFromTheme(initialTheme)
    setForm(next)
    setSavedSnapshot(JSON.stringify(next))
  }, [buildFormFromTheme, initialTheme])

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    },
    [],
  )

  const notifyThemeChange = useCallback(
    (updated: ThemeFormState) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        const theme: ITenantThemeConfig = { primaryColor: updated.primaryColor }
        if (updated.secondaryColor) theme.secondaryColor = updated.secondaryColor
        if (updated.accentColor) theme.accentColor = updated.accentColor
        if (updated.fontFamily) theme.fontFamily = updated.fontFamily
        if (updated.logoUrl) theme.logoUrl = updated.logoUrl
        if (updated.faviconUrl) theme.faviconUrl = updated.faviconUrl
        onThemeChange?.(theme)
      }, THEME_PREVIEW_DEBOUNCE_MS)
    },
    [onThemeChange],
  )

  function updateField<K extends keyof ThemeFormState>(key: K, value: ThemeFormState[K]) {
    const updated = { ...form, [key]: value }
    setForm(updated)
    notifyThemeChange(updated)
  }

  async function handleLogoFile(file: File) {
    if (file.size > MAX_BRANDING_IMAGE_SIZE_BYTES) {
      showToast.error(t('tenant.theme.logoTooLarge', { max: MAX_BRANDING_IMAGE_SIZE_MB }))
      return
    }
    setIsLogoUploading(true)
    try {
      const uploaded = await uploadFileWithPresign(file)
      updateField('logoUrl', uploaded.url)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setIsLogoUploading(false)
    }
  }

  async function handleFaviconFile(file: File) {
    if (file.size > MAX_BRANDING_IMAGE_SIZE_BYTES) {
      showToast.error(t('tenant.theme.faviconTooLarge', { max: MAX_BRANDING_IMAGE_SIZE_MB }))
      return
    }
    setIsFaviconUploading(true)
    try {
      const uploaded = await uploadFileWithPresign(file)
      updateField('faviconUrl', uploaded.url)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setIsFaviconUploading(false)
    }
  }

  async function handleSave() {
    try {
      const themeConfig: ITenantThemeConfig = { primaryColor: form.primaryColor }
      if (form.secondaryColor) themeConfig.secondaryColor = form.secondaryColor
      if (form.accentColor) themeConfig.accentColor = form.accentColor
      if (form.fontFamily) themeConfig.fontFamily = form.fontFamily
      if (form.logoUrl) themeConfig.logoUrl = form.logoUrl
      if (form.faviconUrl) themeConfig.faviconUrl = form.faviconUrl

      await mutation.mutateAsync({ id: tenantId, themeConfig })
      setSavedSnapshot(JSON.stringify(form))
      showToast.success(t('tenant.theme.saveSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const hasChanges = JSON.stringify(form) !== savedSnapshot
  const isSaveDisabled = mutation.isPending || !hasChanges || isLogoUploading || isFaviconUploading

  useEffect(() => {
    onDirtyChange?.(hasChanges)
  }, [hasChanges, onDirtyChange])

  // * VI: Parent chỉ nhận lại ctx khi disabled/pending đổi, nên onSave phải đọc handleSave mới nhất qua ref để không lưu form cũ. | EN: Parent only gets a new ctx when disabled/pending change, so onSave reads the latest handleSave through a ref to avoid saving a stale form.
  const handleSaveRef = useRef(handleSave)
  useEffect(() => {
    handleSaveRef.current = handleSave
  })

  useEffect(() => {
    onSaveReady?.({
      onSave: () => {
        void handleSaveRef.current()
      },
      disabled: isSaveDisabled,
      isPending: mutation.isPending,
    })
  }, [onSaveReady, isSaveDisabled, mutation.isPending])

  return (
    <div className="flex flex-col gap-5">
      {/* Logo */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('tenant.theme.logo')}</Label>
        {form.logoUrl ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewImage({ url: form.logoUrl, alt: t('tenant.theme.logo') })}
              className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              <img
                src={form.logoUrl}
                alt="logo"
                className="h-10 max-w-[120px] object-contain border border-zinc-200 rounded p-1 cursor-zoom-in"
              />
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-zinc-400"
              onClick={() => updateField('logoUrl', '')}
            >
              <X size={14} />
            </Button>
          </div>
        ) : (
          <label className="cursor-pointer inline-block">
            <input
              type="file"
              className="hidden"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                await handleLogoFile(file)
                e.currentTarget.value = ''
              }}
            />
            <Button variant="outline" size="sm" asChild disabled={isLogoUploading}>
              <span>
                {isLogoUploading ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Upload size={14} className="mr-1.5" />
                )}
                {t('tenant.theme.logo')}
              </span>
            </Button>
          </label>
        )}
      </div>

      {/* Favicon */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('tenant.theme.favicon')}</Label>
        {form.faviconUrl ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setPreviewImage({ url: form.faviconUrl, alt: t('tenant.theme.favicon') })
              }
              className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              <img
                src={form.faviconUrl}
                alt="favicon"
                className="h-8 w-8 object-contain border border-zinc-200 rounded p-0.5 cursor-zoom-in"
              />
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-zinc-400"
              onClick={() => updateField('faviconUrl', '')}
            >
              <X size={14} />
            </Button>
          </div>
        ) : (
          <label className="cursor-pointer inline-block">
            <input
              type="file"
              className="hidden"
              accept="image/png,image/x-icon,image/vnd.microsoft.icon"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                await handleFaviconFile(file)
                e.currentTarget.value = ''
              }}
            />
            <Button variant="outline" size="sm" asChild disabled={isFaviconUploading}>
              <span>
                {isFaviconUploading ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Upload size={14} className="mr-1.5" />
                )}
                {t('tenant.theme.favicon')}
              </span>
            </Button>
          </label>
        )}
      </div>

      {/* Primary Color */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('tenant.theme.primaryColor')}</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={form.primaryColor}
            onChange={(e) => updateField('primaryColor', e.target.value)}
            className="h-9 w-16 rounded border border-zinc-200 cursor-pointer p-0.5"
          />
          <span className="text-sm text-zinc-500">{form.primaryColor}</span>
        </div>
      </div>

      {/* Secondary Color */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('tenant.theme.secondaryColor')}</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={form.secondaryColor}
            onChange={(e) => updateField('secondaryColor', e.target.value)}
            className="h-9 w-16 rounded border border-zinc-200 cursor-pointer p-0.5"
          />
          <span className="text-sm text-zinc-500">{form.secondaryColor}</span>
        </div>
      </div>

      {/* Accent Color */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('tenant.theme.accentColor')}</Label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={form.accentColor}
            onChange={(e) => updateField('accentColor', e.target.value)}
            className="h-9 w-16 rounded border border-zinc-200 cursor-pointer p-0.5"
          />
          <span className="text-sm text-zinc-500">{form.accentColor}</span>
        </div>
      </div>

      {/* Font Family */}
      <div className="flex flex-col gap-1.5">
        <Label>{t('tenant.theme.fontFamily')}</Label>
        <Select value={form.fontFamily} onValueChange={(v) => updateField('fontFamily', v)}>
          <SelectTrigger className="w-[240px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AVAILABLE_FONTS.map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Dialog open={Boolean(previewImage)} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-4xl p-2 bg-transparent border-none shadow-none">
          <DialogTitle className="sr-only">{previewImage?.alt ?? t('common.preview')}</DialogTitle>
          <DialogDescription className="sr-only">{t('common.preview')}</DialogDescription>
          {previewImage && (
            <img
              src={previewImage.url}
              alt={previewImage.alt}
              className="w-full max-h-[80vh] object-contain rounded-lg bg-black/60"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
